import classNames from 'classnames'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { InfoIcon } from 'components/icons/Info'
import { TriangleRightFillIcon } from 'components/icons/TriangleRightFIll'
import type { ReactElement } from 'react'

export function BreakoutTableColumnHeader<T>({
  label,
  sortable,
  tooltip,
  alignment = 'left',
  className,
}: {
  label: string
  tooltip?: string | ReactElement
  sortable?: BreakoutTableColumnHeaderSortableBase<T> & { sortingStrategy: T }
  alignment?: 'left' | 'center' | 'right'
  className?: string
}) {
  const isCurrentStrategy =
    sortable && sortable.currentSortingStrategy === sortable.sortingStrategy

  const onMouseDown = () => {
    if (!sortable) return

    if (
      sortable.allowDefaultSortingStrategy &&
      isCurrentStrategy &&
      sortable.currentOrder === 'asc'
    ) {
      sortable.changeSortingStrategy('default', 'desc')
    } else {
      sortable.changeSortingStrategy(
        sortable.sortingStrategy,
        isCurrentStrategy && sortable.currentOrder === 'asc' ? 'desc' : 'asc'
      )
    }
  }

  return (
    <th className={className} scope="col">
      <div
        className={classNames('flex flex-row items-center gap-1 bg-surface', {
          'cursor-pointer': sortable?.sortingStrategy,
          'justify-center text-center': alignment === 'center',
          'justify-end text-right': alignment === 'right',
        })}
        role={sortable ? 'button' : undefined}
        tabIndex={sortable ? 0 : undefined}
        onMouseDown={onMouseDown}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault() // Prevent scrolling on Space
            onMouseDown()
          }
        }}
        aria-pressed={sortable ? isCurrentStrategy : undefined}
      >
        <strong
          className={classNames(
            'text-nowrap text-label-medium text-on-surface-var',
            {
              'select-none': sortable?.sortingStrategy !== undefined,
            }
          )}
        >
          {label}
        </strong>
        {tooltip && (
          <BreakoutTooltip content={tooltip}>
            <div className="mx-[-0.25rem] shrink-0 p-1">
              <InfoIcon
                size={15}
                className={classNames(
                  'min-h-[15px] min-w-[15px] fill-on-surface-var'
                )}
              />
            </div>
          </BreakoutTooltip>
        )}
        {sortable?.sortingStrategy !== undefined && (
          <TriangleRightFillIcon
            size={12}
            className={classNames('shrink-0 rotate-90', {
              'rotate-90':
                isCurrentStrategy && sortable.currentOrder === 'desc',
              '!rotate-270':
                isCurrentStrategy && sortable.currentOrder === 'asc',
              'fill-on-surface-disabled': !isCurrentStrategy,
              'fill-fixed-accent-color': isCurrentStrategy,
            })}
          />
        )}
      </div>
    </th>
  )
}

export type BreakoutTableColumnHeaderSortableBase<T> =
  | {
      currentOrder: 'asc' | 'desc'
      currentSortingStrategy: T | 'default'
      changeSortingStrategy: (
        strategy: T | 'default',
        order: 'asc' | 'desc'
      ) => void
      allowDefaultSortingStrategy: true
    }
  | {
      currentOrder: 'asc' | 'desc'
      currentSortingStrategy: T
      changeSortingStrategy: (strategy: T, order: 'asc' | 'desc') => void
      allowDefaultSortingStrategy?: false
    }
